import * as ApiClient from '@/libs/ApiClient';
import type { AxiosResponse } from 'axios';
import { RouteUrlList } from '@/libs/RouteList';
/**
 * 上傳項目 DTO（簡化版，用於 FileUpload 頁面）
 */
export interface IUploadItemDTO_FileUpload {
    /** 項目ID */
    itemID: number;
    /** 父項目ID */
    parentID: number | null;
    /** 父項目名稱 */
    parentItemName: string | null;
    /** 項目層級 */
    itemLevel: number;
    /** 項目代碼 */
    itemCode: string;
    /** 項目名稱 */
    itemName: string;
    /** 是否為葉節點 */
    isLeafNode: boolean;
    /** 排序順序 */
    sortOrder: number;
    /** 是否啟用 */
    isActive: boolean;
    /** 建立時間 */
    createdTime: string | null;
    /** 更新時間 */
    lastUpdate: string | null;
    /** 說明 */
    description: string | null;
    /** 支援的檔案格式 */
    fileFormat: string | null;
    /** 最大檔案大小 (MB) */
    maxFileSize: number | null;
    /** 最多上傳筆數 */
    maxMultiUpload: number | 1;
    /** 提示 每項目名稱後有「?」圖示，點擊可開啟該項目的示意圖與注意事項 */
    hint: string | null;
    /** OCR 類型 (例如: IdFront, IDCard_Back) */
    ocrType: string | null;
    /** 總筆數 */
    totalCount: number;
    /** 是否允許集保(TDCC)串接 */
    isAllowTdcc: boolean;
}

/**
 * 身份證發證地點選項
 */
export interface ISiteRow {
    /** 地點代碼 (例如: 10001) */
    enum_id: string;
    /** 地點名稱 (例如: 北縣) */
    enum_nm: string;
    /** 地點描述 (例如: 3) */
    enum_desc: string;
}

/**
 * 需要上傳的項目資訊
 */
export interface IRequiredUploadItem {
    /** 項目ID */
    itemID: number;
    /** 項目名稱 */
    itemName: string;
    /** 是否為葉節點 */
    isLeafNode: boolean;
    /** 排序順序 */
    sortOrder: number;
    /** 最多上傳筆數 */
    maxMultiUpload: number;
    /** 文件唯一識別碼 (上傳成功後回傳) */
    documentIdFirstOrDefault: string | null;
    /** 檔案雜湊值 (SHA256) */
    fileHash: string | null;
    /**  檔案 MIME 類型 */
    mimeType: string | null;
    /** 上傳時間 */
    uploadTime: Date | null;
    /** 是否正在上傳中 */
    isUploading: boolean;
    /** 提示 每項目名稱後有「?」圖示，點擊可開啟該項目的示意圖與注意事項 */
    hint: string | null;
    /** OCR 類型 (例如: IdFront, IDCard_Back) */
    ocrType: string | null;
    /** 錯誤訊息 */
    errorMessage: string | null;
    /** 是否允許集保(TDCC)串接 */
    isAllowTdcc: boolean;
}

/**
 * 檔案上傳頁面資料回應
 */
export interface IFileUploadViewDataResponse {
    /** 回應狀態 */
    status: boolean;
    /** 錯誤代碼 (0: 成功, -1: 無權限, 1: 無資料) */
    code: number;
    /** 例外訊息 */
    exception: string | null;
    /** 訊息 */
    message: string | null;
    /** 是否為行動裝置登入 */
    isMobileLogin: boolean;
    /** 身份證字號 */
    ssn: string;
    /** 身份證發證年份 (民國) */
    idCheck_Y: number;
    /** 身份證發證月 */
    idCheck_M: number;
    /** 身份證發證日 */
    idCheck_D: number;
    /** 身份證發證地點 */
    idCheck_P: string;
    /** 身份證發證類型 (初發:1 補發:2 換發:3) */
    idCheck_T: string;
    /** 身份證資訊是否已驗證 */
    isVerified: boolean;
    /** 身份證發證地點列表 */
    moiSiteList: ISiteRow[] | null;
    /** 需要上傳的項目列表 (用於顧客端顯示) */
    requiredUploadItems: IRequiredUploadItem[] | null;
    /** 已過濾的上傳項目（根據選中的業務過濾，階層結構）用於頁面重新整理後恢復「新增上傳檔案」按鈕選項 */
    filteredUploadItems: IUploadItemDTO_FileUpload[] | null;
    /** Token (此值已儲存於資料庫) */
    token: string | null;
    /** QR Code 資料 (Base64編碼的PNG圖片，可直接用於前端顯示) */
    qrCodeBase64: string | null;
    /** 手機端驗證用的完整URL / APlus Mobile App專用URL 同QR Code 圖片*/
    mobileVerifyUrl: string | null;
    /** 是否為有效證券帳戶（有帳號才可串接集保） */
    isValidAccount: boolean;
    /** 案件級別的集保交易序號（有值表示已授權） */
    caseTxID: string | null;
    /** TDCC Callback 結果（一次性旗標；null = 非 Callback 來源，1 = 成功，0 = 未完成） */
    tdccCallbackResult?: 1 | 0 | null;
}

/**
 * 檔案上傳 - 取得頁面資料 API
 *
 * @description 取得檔案上傳頁面所需的資料，包含身份證資訊、需上傳項目、Token 和 QR Code
 * @description 此 API 從 Session 和登入資訊獲取所需參數，不需要傳遞額外參數
 * @returns Promise<IFileUploadViewDataResponse>
 */
export async function FileUploadViewData(refreshToken: boolean = true): Promise<IFileUploadViewDataResponse> {
    const url = RouteUrlList.API_FileUploadViewData.path;
    const config = {
        params: {
            showOverlay: ApiClient.paramTypeEnum.true,
            getServiceAlert: ApiClient.paramTypeEnum.true
        }
    };

    return await ApiClient.post(
        url,
        {
            refreshToken: refreshToken
        },
        config
    ).then((res: AxiosResponse<IFileUploadViewDataResponse>) => {
        return res.data;
    });
}

export default FileUploadViewData;
